import React, { useCallback, useEffect, useState } from 'react';
import css from './AddressForm.module.scss';
import '../../css/themes.scss';
import { strings } from 'src/localization';
// import { getStatesDropDownList } from '@reynolds/statelisthelper';
import { getStateOrProvinceDropDownList } from 'src/helpers/StateProvinceHelper/StateProvinceHelper';
import { TextField, SelectList, IDropDownItem, LoadingProgress } from '@framework-controls/core';
import { ApptPULoc, ApptDOLoc, ZIPSearchReturn } from 'src/models';

// RegEx
const ATest = new RegExp(/[^a-zA-Z ]/g);
const NTest = new RegExp(/[^0-9]/g);
const ANTest = new RegExp(/[^a-zA-Z0-9 ]/g);
const CAZipTest = new RegExp(/[A-Za-z]\d[A-Za-z]\s\d[A-Za-z]\d/i);

export interface IProps {
    type: string;
    sameAsCheckBox: boolean;
    address: string;
    address2: string;
    zipPostal: string;
    cityTown: string;
    stateProvince: string;
    locale: string;
    puProcess?: boolean;
    doProcess?: boolean;
    zipReturn: ZIPSearchReturn;
    countryCode?: string;
    addressBlurHandler: (elementText: string, formName: string) => void;
    dropdownLogHandler: (elementText: string, eventAction: string) => void;
    setPUProcess?: (puProcess: boolean) => void;
    setDOProcess?: (doProcess: boolean) => void;
    setPUProceedFlag?: (puProceedFlag: boolean) => void;
    setDOProceedFlag?: (puProceedFlag: boolean) => void;
    updatePickupInfo?:(inp:ApptPULoc) => void;
    updateDeliveryInfo?:(inp:ApptDOLoc) => void;
    zipSearch: (zipCode:string) => void;
    clearZipSearch: () => void;
}

const AddressForm = (props: IProps) => {
    // Error Initialization
    interface IErrorDictionary {
        [field: string]: {
            error: boolean;
            helperText: string;
            isRequired?: boolean;
        }
    }

    const initialError: IErrorDictionary = {
        'streetAddress': { error: false, helperText: '' },
        'zipCode': { error: false, helperText: '' },
        'city': { error: false, helperText: '' },
        'state': { error: false, helperText: '' },
    };

    // Destructuring props
    const {type, sameAsCheckBox, address, address2, zipPostal, cityTown, stateProvince,
        locale, puProcess, doProcess, zipReturn, countryCode} = props;

    // Variable Initialization
    const [streetAddress, setStreetAddress] = useState<string>(address ? address : '');
    const [streetAddress2, setStreetAddress2] = useState<string>(address2 ? address2 : '');
    const [zipCode, setZIPCode] = useState<string>(zipPostal ? zipPostal : '');
    const [city, setCity] = useState<string>(cityTown ? cityTown : '');
    const [state, setState] = useState<string>(stateProvince ? stateProvince : '');
    const [errors, setErrors] = useState(initialError);
    const [stateList, setStateList] = useState<IDropDownItem[]>([]);
    const hidden = props.type === 'Pickup' ? false : ((props.type === 'Delivery' && sameAsCheckBox) ? true : false);
    const [onEntry, setOnEntry] = useState<boolean>(true);
    const [zipSearching, setZIPSearching] = useState<boolean>(false);

    if (onEntry) {
        // get the list of states/provinces
        // if (locale === 'fr-CA' || locale === 'en-CA') {
        //     country = 'CA';
        // }
        setStateList(getStateOrProvinceDropDownList(locale));
        setOnEntry(false);
    }

    useEffect(() => {
        setState('');
        setStateList(getStateOrProvinceDropDownList(locale));
    }, [locale]);

    // Field Validation
    const blurHandler = useCallback((id: string, value: string) => {
        let hasError = false;
        let helperText = '';
        let fieldErrors = {...errors};
        let testValue = '';
        let elementText = '';
        switch (id) {
            case (type + 'streetAddress'):
                // Alphanumeric with spaces
                elementText = 'streetAddress';
                if (value.length < 1) {
                    hasError = true;
                    helperText = strings.StreetAddressErrMsg2;
                } else {
                    if (value.match(ANTest)) {
                        hasError = true;
                        helperText = strings.StreetAddressErrMsg1;
                    } else {
                        hasError = false;
                        helperText = '';
                    }
                }
                break;
            case (type + 'streetAddress2'):
                elementText = 'streetAddress2';
                // Alphanumeric with spaces and special char
                break;
            case (type + 'zipCode'):
                elementText = 'zipCode';
                // if (locale === 'en-US') {
                if (countryCode === 'US') {
                    // Numeric - US
                    if ((value.length === 5 || value.length === 9) && !value.match(NTest)) {
                        if (value.length === 9) {
                            const returnValue = value.slice(0, 5) + '-' + value.slice(5, 9);
                            setZIPCode(returnValue);
                        }
                        hasError = false;
                        helperText = '';
                    } else {
                        hasError = true;
                        helperText = strings.ZIPCodeErrMsg;
                    }
                    if (hasError === false) {
                        props.zipSearch(value.slice(0, 5));
                        setZIPSearching(true);
                    }
                } else {
                    // Alphanumeric - CA
                    if (value.length === 6 || value.length === 7) {
                        if (value.length === 6) {
                            testValue = value.slice(0, 3) + ' ' + value.slice(3, 7);
                            if (testValue.match(CAZipTest)){
                                setZIPCode(testValue);
                            }
                        } else {
                            testValue = value;
                        }
                        if (testValue.match(CAZipTest)) {
                            hasError = false;
                            helperText = '';
                        } else {
                            hasError = true;
                            helperText = strings.PostalCodeErrMsg;
                        }
                    }
                    if (hasError === false) {
                        props.zipSearch(testValue);
                        setZIPSearching(true);
                    }
                }
                break;
            case (type + 'city'):
                elementText = 'city';
                // Alpha with spaces
                if (value.length < 1) {
                    hasError = true;
                    helperText = strings.CityErrMsg;
                } else {
                    if (!value.match(ATest)) {
                        hasError = false;
                        helperText = '';
                    } else {
                        hasError = true;
                        helperText = strings.CityErrMsg;
                    }
                }
                break;
            case (type + 'state'):
                elementText = 'state';
                break;
            default:
                break;
        }
        fieldErrors = {
            ...fieldErrors,
            [id.split(type)[1]]: {
                error: hasError,
                helperText: helperText,
            },
        };
        setErrors(fieldErrors);
        props.addressBlurHandler(type + 'AddressForm', elementText);
    }, [errors, type, props.addressBlurHandler, props.zipSearch, countryCode]);

    // Field Change Handling
    const streetAddressChangeHandler = useCallback((id: string, value: string) => {
        // Alphanumeric with spaces
        const testValue = value.replace(ANTest, '');
        setStreetAddress(testValue);
    }, []);

    const streetAddress2ChangeHandler = useCallback((id: string, value: string) => {
        // Alphanumeric with spaces and special characters
        setStreetAddress2(value);
    }, []);

    const zipCodeChangeHandler = useCallback((id: string, value: string) => {
        // if (locale === 'en-US') {
        if (countryCode === 'US') {
            // Numeric
            let testValue = value.replace(NTest, '');
            if (testValue.length < 10) {
                testValue = testValue.replace('-', '');
                setZIPCode(testValue);
            }
        } else {
            // Alphanumeric
            const testValue = value.replace(ANTest, '');
            if (testValue.length < 8) {
                setZIPCode(testValue);
            }
        }
    }, [countryCode]);

    const cityChangeHandler = useCallback((id: string, value: string) => {
        // Alpha with spaces
        const testValue = value.replace(ATest, '');
        setCity(testValue);
    }, []);

    const stateChangeHandler = useCallback((id: string, value: string) => {
        // Alpha (Dropdown list)
        setState(value);
        props.dropdownLogHandler(value, 'click');
    }, [props.dropdownLogHandler]);

    // Changing the Proceed Flag once all info is entered
    useEffect (() => {
        if (type === 'Pickup') {
            if (streetAddress?.length && !errors['streetAddress'].error && city?.length && !errors['city'].error &&
            zipCode?.length && !errors['zipCode'].error && state?.length && !errors['state'].error && !zipSearching) {
                props.setPUProceedFlag!(true);
            } else {
                props.setPUProceedFlag!(false);
            }
        } else {
            if (streetAddress?.length && !errors['streetAddress'].error && city?.length && !errors['city'].error &&
            zipCode?.length && !errors['zipCode'].error && state?.length && !errors['state'].error &&
            !sameAsCheckBox && !zipSearching) {
                props.setDOProceedFlag!(true);
            } else {
                props.setDOProceedFlag!(false);
            }
        }
    }, [streetAddress?.length, city?.length, state?.length, zipCode?.length, errors, sameAsCheckBox,
        props.setDOProceedFlag, props.setPUProceedFlag, type, zipSearching]);

    // Proceed Section, updates state
    useEffect (() => {
        if (type === 'Pickup' && puProcess === true) {
            if (sameAsCheckBox === true) {
                const apptDOInfo: ApptDOLoc = {
                    doAddress: streetAddress,
                    doAddress2: streetAddress2,
                    doZipPostal: zipCode,
                    doCity: city,
                    doStateProvince: state,
                };
                props.updateDeliveryInfo!(apptDOInfo);
            }
            const apptPUInfo: ApptPULoc = {
                puAddress: streetAddress,
                puAddress2: streetAddress2,
                puZipPostal: zipCode,
                puCity: city,
                puStateProvince: state,
            };
            props.updatePickupInfo!(apptPUInfo);
            props.setPUProcess!(false);
        } else if (type === 'Delivery' && doProcess === true) {
            if (sameAsCheckBox !== true) {
                const apptDOInfo: ApptDOLoc = {
                    doAddress: streetAddress,
                    doAddress2: streetAddress2,
                    doZipPostal: zipCode,
                    doCity: city,
                    doStateProvince: state,
                };
                props.updateDeliveryInfo!(apptDOInfo);
            }
            props.setDOProcess!(false);
        }
    }, [doProcess, puProcess, sameAsCheckBox, type, city, state, streetAddress, streetAddress2, zipCode, props.updatePickupInfo,
        props.updateDeliveryInfo, props.setDOProcess, props.setPUProcess]);

    // ZIP search handling
    useEffect(() => {
        if (zipSearching && zipReturn !== null && zipReturn !== undefined &&
            (zipReturn.city?.length || zipReturn.state?.length)) {
            if (zipReturn.city === ' ') {
                setCity('');
            } else {
                const testValue = zipReturn.city.replace(ATest, '');
                setCity(testValue);
            }
            let fieldErrors = {...errors};
            fieldErrors = {
                ...fieldErrors,
                'city': {
                    error: false,
                    helperText: '',
                },
            };
            setErrors(fieldErrors);
            if (zipReturn.state === ' ') {
                setState('');
            } else {
                setState(zipReturn.state);
            }
            setZIPSearching(false);
            props.clearZipSearch();
        }
    }, [zipSearching, zipReturn, props.clearZipSearch, errors]);

    return (
        <div id={type} className={css.menuContent}>
            {zipSearching ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
            <div id='inputArea' className={css.inputArea} hidden={hidden}>
                <div className={css.inputClasses}>
                    <TextField
                        id={type + 'streetAddress'}
                        label={strings.StreetAddress}
                        value={streetAddress}
                        size='full'
                        onChange={streetAddressChangeHandler}
                        type='text'
                        onBlur={blurHandler}
                        error={errors['streetAddress'].error}
                        helperText={errors['streetAddress'].error ?
                            errors['streetAddress'].helperText : ''}
                        disabled={hidden}
                    />
                </div>
                <div className={css.inputClasses}>
                    <TextField
                        id={type + 'streetAddress2'}
                        label={strings.StreetAddress2}
                        value={streetAddress2}
                        size='full'
                        onChange={streetAddress2ChangeHandler}
                        type='text'
                        onBlur={blurHandler}
                        disabled={hidden}
                    />
                </div>
                <div className={css.inputClasses}>
                    <TextField
                        id={type + 'zipCode'}
                        label={strings.ZipCode}
                        value={zipCode}
                        size='full'
                        onChange={zipCodeChangeHandler}
                        type='text'
                        onBlur={blurHandler}
                        error={errors['zipCode'].error}
                        helperText={errors['zipCode'].error ?
                            errors['zipCode'].helperText : ''}
                        disabled={hidden}
                    />
                </div>
                <div className={css.inputClasses}>
                    <TextField
                        id={type + 'city'}
                        label={strings.City}
                        value={city}
                        size='full'
                        onChange={cityChangeHandler}
                        type='text'
                        onBlur={blurHandler}
                        error={errors['city'].error}
                        helperText={errors['city'].error ?
                            errors['city'].helperText : ''}
                        disabled={hidden}
                    />
                </div>
                <div className={css.inputClasses}>
                    <SelectList
                        id={type + 'state'}
                        key={state}
                        className={css.selectList}
                        dropDownList={stateList}
                        onChange={stateChangeHandler}
                        value={state}
                        label={strings.State}
                        size='full'
                        required
                    />
                </div>
            </div>
        </div>
    );
};

export default AddressForm;